import styled from "styled-components";

// Plan header gradients keep the Figma angle; the colour pairs match the
// per-tier headers already used by the plan cards.
const TIER_GRADIENTS = {
  free: "rgb(222, 225, 229) 0%, rgb(242, 243, 245) 100%",
  core: "rgb(224, 214, 238) 0%, rgb(244, 240, 250) 100%",
  plus: "rgb(206, 222, 237) 0%, rgb(239, 244, 249) 100%",
  pro: "rgb(227, 232, 210) 0%, rgb(243, 245, 235) 100%",
  other: "rgb(206, 222, 237) 0%, rgb(239, 244, 249) 100%",
};

export const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  font-family: "Geist", sans-serif;
  color: #181d27;

  .divider {
    height: 1px;
    margin: 28px 0;
    background: rgba(0, 0, 0, 0.06);
  }

  .panel-notice {
    margin: 12px 0 0;
    color: #535861;
    font-size: 12px;
    line-height: 18px;
  }

  .panel-heading {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: -0.2px;
    color: #181d27;
  }
`;

const buttonBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 36px;
  padding: 8px 14px;
  border-radius: 8px;
  font-family: "Geist", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const DarkButton = styled.button`
  ${buttonBase}
  background: #181d27;
  border: 1.8px solid rgba(255, 255, 255, 0.12);
  color: #fff;
`;

export const OutlineButton = styled.button`
  ${buttonBase}
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.09);
  color: rgba(10, 13, 18, 0.7);
`;

export const PlanHeaderCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 78px;
  padding: 16px 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  background: linear-gradient(
    6.95deg,
    ${({ $tier }) => TIER_GRADIENTS[$tier] || TIER_GRADIENTS.other}
  );

  .plan-identity {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .plan-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 44px;
    height: 44px;
  }

  .plan-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .plan-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .plan-title {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: -0.1px;
  }

  .plan-interval {
    padding: 2px 6px;
    border: 1px solid rgba(10, 13, 18, 0.1);
    border-radius: 9999px;
    background: #fafafa;
    color: #414651;
    font-size: 10px;
    line-height: 14px;
  }

  .plan-description {
    color: #535861;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: -0.1px;
  }

  @media (max-width: 540px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const RenewalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 20px;
  padding: 16px 20px;
  border: 1px solid rgba(0, 0, 0, 0.09);
  border-radius: 12px;
  background: #fafafa;

  .renewal-text {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #414651;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.1px;
  }

  svg {
    flex-shrink: 0;
  }
`;

export const PaymentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 16px;

  .card-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .card-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: 1px solid rgba(10, 13, 18, 0.1);
    border-radius: 8px;
    background: #fafafa;
  }

  .card-text {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .card-title {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.1px;
  }

  .card-sub {
    color: #a3a7ae;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: -0.1px;
  }

  .card-warning {
    color: #b42318;
    font-size: 12px;
    line-height: 18px;
  }
`;

export const HistoryTableWrapper = styled.div`
  margin-top: 16px;
  border: 1px solid rgba(0, 0, 0, 0.09);
  border-radius: 12px;
  overflow: hidden;

  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }

  th {
    padding: 12px 16px;
    background: #fafafa;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    color: #a3a7ae;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: -0.1px;
    text-align: left;
  }

  td {
    padding: 12px 16px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    color: #414651;
    font-size: 14px;
    line-height: 24px;
    letter-spacing: -0.1px;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  /* Proportions of the Figma grid (187 / 133 / 133 / 107px, 12px gaps). */
  .col-date {
    width: 31.64%;
  }

  .col-amount,
  .col-status {
    width: 23.15%;
  }

  .col-invoice {
    text-align: right;
  }

  .view-link {
    padding: 0;
    border: none;
    background: transparent;
    color: #414651;
    font-family: "Geist", sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: -0.1px;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .history-empty {
    padding: 24px 16px;
    color: #a3a7ae;
    font-size: 14px;
    text-align: center;
  }

  .history-more {
    display: block;
    width: 100%;
    padding: 12px 16px;
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    background: #fafafa;
    color: #414651;
    font-family: "Geist", sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
  }
`;

const STATUS_STYLES = {
  paid: { bg: "#eef7ee", border: "#cbe7cc", color: "#316434" },
  failed: { bg: "#fef3f2", border: "#fecdca", color: "#b42318" },
  neutral: { bg: "#fafafa", border: "rgba(10, 13, 18, 0.1)", color: "#414651" },
};

export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border: 1px solid ${({ $tone }) => STATUS_STYLES[$tone].border};
  border-radius: 9999px;
  background: ${({ $tone }) => STATUS_STYLES[$tone].bg};
  color: ${({ $tone }) => STATUS_STYLES[$tone].color};
  font-size: 12px;
  line-height: 18px;
  letter-spacing: -0.1px;
`;
